import { CloseModalButton, useModal } from "../modal";
import { Accessor, createSignal, Setter, Show } from "solid-js";
import { OfferConstruct, VoucherProductMetadata } from "~/server/types/brand";
import { goBack } from "~/shared_states/modal";
import { Optional } from "~/types";
import { getConsistentDay, getOrdinalDay } from "~/utils/date";
import { createInputHandler } from "~/utils/input";
import { ThreeDotLoader } from "~/widgets/button";
import { Spacer } from "~/widgets/spacer";

type UserInfoModalProps = {
  onClickSubmit: (profileUpdateParams: any) => void;
  email: string | null;
  phone: string | null;
  name: string | null;
  subtitle: string;
  isOfferConstructAvailable: boolean;
  offerConstructType: "ONE_TIME_REWARD" | "SUBSCRIPTION_REWARD" | undefined;
  offerConstructTenure?: Accessor<Optional<number>>;
  redemptionAmount: Accessor<Optional<number>>;
  dueAmount: Accessor<Optional<number>>;
  selectedAmount: Accessor<Optional<number>>;
  brandDetails?: VoucherProductMetadata;
  clientLogoUrl?: string;
  setShowLoader: Setter<boolean>;
  showLoader: Accessor<boolean>;
  errors: Accessor<{ [key: string]: string }>;
  setErrors: Setter<{ [key: string]: string }>;
};

export default function TataUserInfoModal(props: UserInfoModalProps) {
  const [name, setName] = createSignal<string>(props.name ?? "");
  const [email, setEmail] = createSignal<string>(props.email ?? "");
  const [phone, setPhone] = createSignal<string>(props.phone ?? "");
  const [visitedFields, setVisitedFields] = createSignal<{
    [key: string]: boolean;
  }>({});
  let nameInput: HTMLInputElement | undefined;
  let emailInput: HTMLInputElement | undefined;
  let phoneInput: HTMLInputElement | undefined;

  const { errors, setErrors } = props;

  function isNameAlreadyPresent() {
    return !!props.name;
  }

  function isEmailAlreadyPresent() {
    return !!props.email;
  }

  function isPhoneAlreadyPresent() {
    return !!props.phone;
  }

  const updateUserInfo = async () => {
    let body: any = {};
    if (!isNameAlreadyPresent()) {
      body = {
        ...body,
        firstName: name().split(" ")[0],
        lastName: name().split(" ").slice(1).join(" "),
      };
    }
    if (!isEmailAlreadyPresent()) {
      body = {
        ...body,
        email: email(),
      };
    }
    if (!isPhoneAlreadyPresent()) {
      body = {
        ...body,
        phoneNumber: phone(),
      };
    }

    props.onClickSubmit(body);
  };

  const validateName = (name: string) => {
    const nameRegex = /^[A-Za-z\s]{2,50}$/;
    return nameRegex.test(name.trim());
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^[1-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validateAndUpdateErrors = () => {
    const newErrors: { [key: string]: string } = {};
    const visited = visitedFields();

    if (visited["name"]) {
      if (!name()) {
        newErrors["name"] = "Please enter your full name";
      } else if (!validateName(name())) {
        newErrors["name"] = "Please enter your full name";
      }
    }

    if (visited["email"]) {
      if (!email()) {
        newErrors["email"] = "Please enter your email address";
      } else if (!validateEmail(email())) {
        newErrors["email"] = "Please enter a valid email address";
      }
    }

    if (visited["phone"]) {
      if (!phone()) {
        newErrors["phone"] = "Please enter your Whatsapp phone no.";
      } else if (!validatePhone(phone())) {
        newErrors["phone"] = "Please a valid phone no.";
      }
    }

    setErrors(newErrors);
    return newErrors;
  };

  const errorStrip = (field: string) => {
    return (
      <Show when={errors()[field]}>
        <div class="py-1 text-f12Bold text-errorDark">{errors()[field]}</div>
      </Show>
    );
  };

  const scrollIntoViewSmooth = (element: HTMLElement) => {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <form onSubmit={onSubmit}>
      <div class="flex flex-col bg-baseTertiaryLight text-basePrimaryDark ">
        <div class="flex w-full flex-row items-start justify-between p-4 md:p-6">
          <Show when={!props.isOfferConstructAvailable}>
            <span class="text-h4 text-basePrimaryDark">
              Just one more step
              <br />
              <span class="text-medium text-textNormal">{props.subtitle}</span>
            </span>
          </Show>
          <Show when={props.isOfferConstructAvailable}>
            <span class="flex flex-col items-start justify-center gap-2 font-jakartaSans text-[24px] font-semibold leading-normal tracking-[-0.56px] text-basePrimaryDark md:flex md:text-[28px]">
              <img src={props.clientLogoUrl} class="h-7 w-[132px]" alt="" />{" "}
              {props.offerConstructType === "SUBSCRIPTION_REWARD"
                ? "monthly offer"
                : "NeuCard Offer"}{" "}
            </span>
          </Show>
          <CloseModalButton onClick={() => goBack()} />
        </div>

        <Show when={props.isOfferConstructAvailable}>
          <div class="px-4 pb-2 md:px-6">
            <div class="flex items-center gap-1">
              <p class="text-nowrap text-[11px] font-medium uppercase leading-[140%] tracking-[0.88px] text-textNormal">
                Exclusively for you
              </p>
              <div class="h-px w-full darkTheme:bg-baseTertiaryDark dark:bg-basePrimaryMedium">
                &nbsp;
              </div>
            </div>
            <div class="mt-2 flex items-center justify-between">
              <div class="flex flex-col gap-1">
                <Show when={props.offerConstructType === "SUBSCRIPTION_REWARD"}>
                  <p class="text-[11px] font-semibold leading-[140%] tracking-[-0.11px] text-[#24DD7D]">
                    {props.offerConstructTenure?.()} months offer
                  </p>
                </Show>
                <p class="text-bold ">
                  ₹{props.selectedAmount?.()} {props.brandDetails?.title} gift
                  card
                </p>
                <p class="text-normal">
                  in ₹{props.dueAmount?.()}{" "}
                  <span class="line-through">
                    ₹{props.redemptionAmount?.()}
                  </span>
                </p>
              </div>
              <div class="flex items-center justify-center px-[5px] py-0">
                <img
                  class="h-[52px] min-w-[52px] rounded-full"
                  src={props.brandDetails?.logoUrl}
                  alt={props.brandDetails?.title}
                />
              </div>
            </div>
          </div>
        </Show>

        <div class="flex flex-col gap-4 px-4 py-5 md:px-6">
          <Show when={props.isOfferConstructAvailable}>
            <div class="flex items-center gap-1">
              <p class="text-nowrap text-[11px] font-medium uppercase leading-[140%] tracking-[0.88px] text-textNormal">
                Delivery details
              </p>
              <div class="h-px w-full darkTheme:bg-baseTertiaryDark dark:bg-basePrimaryMedium">
                &nbsp;
              </div>
            </div>
          </Show>
          <div>
            <label class="text-mediumBold text-textDark">Your name</label>
            <Spacer height={8} />
            <input
              ref={nameInput}
              type="text"
              value={name()}
              autocomplete="off"
              disabled={isNameAlreadyPresent()}
              class={`autofill-text-fix w-full rounded-[12px] border bg-baseTertiaryDark px-4 py-3 !text-[16px] text-bold text-textDark placeholder:text-normal placeholder:text-textNormal disabled:bg-baseTertiaryMedium dark:border-basePrimaryDark ${
                errors()["name"]
                  ? "border-errorDark"
                  : "border-baseTertiaryDark"
              } `}
              placeholder="Enter full name"
              onInput={(e) => {
                createInputHandler(setName)(e);
                setVisitedFields((prev) => ({ ...prev, name: true }));
                if (errors()["name"]) {
                  validateAndUpdateErrors();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Tab" && !e.shiftKey && emailInput) {
                  e.preventDefault();
                  emailInput.focus();
                  scrollIntoViewSmooth(emailInput);
                }
              }}
              onBlur={() => {
                setVisitedFields((prev) => ({ ...prev, name: true }));
                validateAndUpdateErrors();
              }}
            />
            {errorStrip("name")}
          </div>
          <div>
            <label class="text-mediumBold text-textDark">Your email</label>
            <Spacer height={8} />
            <input
              disabled={isEmailAlreadyPresent()}
              ref={emailInput}
              value={email()}
              type="email"
              autocomplete="off"
              class={`autofill-text-fix w-full rounded-[12px] border bg-baseTertiaryDark px-4 py-3 !text-[16px] text-bold text-textDark placeholder:text-normal placeholder:text-textNormal disabled:bg-baseTertiaryMedium dark:border-basePrimaryDark ${
                errors()["email"]
                  ? "border-errorDark"
                  : "border-baseTertiaryDark"
              } `}
              placeholder="Enter your email id"
              onInput={(e) => {
                createInputHandler(setEmail)(e);
                setVisitedFields((prev) => ({ ...prev, email: true }));
                if (errors()["email"]) {
                  validateAndUpdateErrors();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Tab") {
                  e.preventDefault();
                  if (!e.shiftKey && phoneInput) {
                    phoneInput.focus();
                    scrollIntoViewSmooth(phoneInput);
                  } else if (e.shiftKey && nameInput) {
                    nameInput.focus();
                    scrollIntoViewSmooth(nameInput);
                  }
                }
              }}
              onBlur={() => {
                setVisitedFields((prev) => ({ ...prev, email: true }));
                validateAndUpdateErrors();
              }}
            />
            <Show when={props.isOfferConstructAvailable && !errors()["email"]}>
              <p class="pt-2 text-f12Bold text-textNormal">
                We’ll send your gift card to this email. No spam, promise.
              </p>
            </Show>
            {errorStrip("email")}
          </div>
          <div>
            <label class="text-mediumBold text-textDark">Phone no.</label>
            <Spacer height={8} />
            <input
              disabled={isPhoneAlreadyPresent()}
              ref={phoneInput}
              value={phone()}
              type="tel"
              autocomplete="off"
              class={`autofill-text-fix w-full rounded-[12px] border bg-baseTertiaryDark px-4 py-3  !text-[16px] text-bold text-textDark placeholder:text-normal placeholder:text-textNormal disabled:bg-baseTertiaryMedium dark:border-basePrimaryDark ${
                errors()["phone"]
                  ? "border-errorDark"
                  : "border-baseTertiaryDark"
              } `}
              placeholder="10 digit phone no."
              onInput={(e) => {
                createInputHandler(setPhone)(e);
                setVisitedFields((prev) => ({ ...prev, phone: true }));
                if (errors()["phone"]) {
                  validateAndUpdateErrors();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Tab" && e.shiftKey && emailInput) {
                  e.preventDefault();
                  emailInput.focus();
                  scrollIntoViewSmooth(emailInput);
                }
              }}
              onBlur={() => {
                setVisitedFields((prev) => ({ ...prev, phone: true }));
                validateAndUpdateErrors();
              }}
            />
            {errorStrip("phone")}
          </div>
        </div>
        <Show when={!props.isOfferConstructAvailable}>
          <div class="px-4 text-f12 text-textNormal md:px-6">
            Your privacy is our priority. We never share your data with anyone
          </div>
        </Show>
        <Show
          when={
            props.isOfferConstructAvailable &&
            props.offerConstructType === "SUBSCRIPTION_REWARD"
          }
        >
          <div class="mx-4 flex flex-col items-start gap-4 self-stretch rounded-[10px] border border-[#3555FF] bg-[#0A1343] p-3 px-3 text-medium text-[#5BCBFF] md:mx-6 md:px-6">
            You'll get one gift card now, and the rest on the{" "}
            {`${getOrdinalDay(getConsistentDay(new Date()))}`} each month.
          </div>
          <p class="mx-4 mt-7 text-medium text-textNormal md:mx-6">
            Click 'Continue' to authorize a ₹{props.dueAmount?.()} monthly
            deduction via VISA. Cancel anytime using the instructions in your
            card delivery email.
          </p>
        </Show>
        <div class="sticky bottom-[0px] left-0 right-0 z-10 m-auto h-[96px] w-[94%] rounded-b-2xl border-t border-baseTertiaryDark bg-baseLight  p-6 md:w-[95%]">
          <button
            type="submit"
            class={`flex h-12 w-full items-center justify-center rounded-full bg-basePrimaryDark py-3 text-f16Bold text-baseTertiaryLight disabled:cursor-not-allowed disabled:bg-[#212427] disabled:py-4 disabled:text-[#313538]`}
            disabled={props.showLoader() || Object.keys(errors()).length > 0}
            onClick={onSubmit}
          >
            <Show
              when={!props.showLoader()}
              fallback={
                <div class="flex h-6 content-center items-center justify-center">
                  <ThreeDotLoader color="#999" />
                </div>
              }
            >
              Continue
            </Show>
          </button>
        </div>
      </div>
    </form>
  );

  function onSubmit(e: Event) {
    e.preventDefault();
    if (props.showLoader() || Object.keys(errors()).length > 0) return;
    // Mark all fields as visited when clicking submit
    setVisitedFields({ name: true, email: true, phone: true });
    const newErrors = validateAndUpdateErrors();
    if (Object.keys(newErrors).length > 0) return;
    updateUserInfo();
  }
}
